import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'

interface SidebarEvent extends CustomEvent {
  detail: {
    id: string
    action: 'open' | 'close' | 'toggle'
  }
}

interface TurboBeforeRenderEvent extends CustomEvent {
  detail: {
    newBody: HTMLBodyElement
  }
}

export class SidebarController extends Controller<HTMLElement> {
  initialOpen = false
  initialMobileOpen = false
  breakpoint = 768
  open = false

  connect(): void {
    this.initialOpen = this.element.dataset.initialOpen !== 'false'
    this.initialMobileOpen = this.element.dataset.initialMobileOpen === 'true'

    if (this.element.dataset.breakpoint) {
      this.breakpoint = parseInt(this.element.dataset.breakpoint)
    }

    this.open =
      this.breakpoint > 0
        ? window.innerWidth >= this.breakpoint
          ? this.initialOpen
          : this.initialMobileOpen
        : this.initialOpen

    this.addDocumentEventListener()
    document.addEventListener('turbo:before-render', this.beforeTurboRender)
    this.addMenuItemClickListener()

    this.updateState(this.element)
    this.element.dataset.sidebarInitialized = 'true'
    // this.element.dispatchEvent(new CustomEvent('basecoat:initialized'))
  }

  disconnect(): void {
    document.removeEventListener('turbo:before-render', this.beforeTurboRender)
    document.removeEventListener('basecoat:sidebar', this.toggleSidebar)
    this.element.removeEventListener('click', this.menuItemClickListener)
  }

  get sidebarId(): string {
    return this.element.id
  }

  updateState = (sidebar: HTMLElement) => {
    sidebar.setAttribute('aria-hidden', `${!this.open}`)
    sidebar.dataset.initialOpen = `${this.open}`
    if (this.open) {
      sidebar.removeAttribute('inert')
    } else {
      sidebar.setAttribute('inert', '')
    }
  }

  setState = (state: boolean) => {
    this.open = state
    this.updateState(this.element)
    this.persistState()
  }

  private beforeTurboRender = (event: Event) => {
    const { newBody } = (event as TurboBeforeRenderEvent).detail ?? {}
    if (!newBody) return

    const sidebar = newBody.querySelector('#sidebar')
    if (!(sidebar instanceof HTMLElement)) return

    this.updateState(sidebar)
  }

  toggleSidebar = (event: Event) => {
    const customEvent = event as SidebarEvent

    if (customEvent.detail?.id && customEvent.detail.id !== this.sidebarId)
      return

    switch (customEvent.detail?.action) {
      case 'open':
        this.setState(true)
        break
      case 'close':
        this.setState(false)
        break
      default:
        this.setState(!this.open)
        break
    }
  }

  private addDocumentEventListener = () => {
    document.addEventListener('basecoat:sidebar', this.toggleSidebar)
  }

  private addMenuItemClickListener = () => {
    this.element.addEventListener('click', this.menuItemClickListener)
  }

  private menuItemClickListener = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const nav = this.element.querySelector('nav')
    const isMobile = window.innerWidth < this.breakpoint

    if (
      isMobile &&
      target.closest('a, button') &&
      !target.closest('[data-keep-mobile-sidebar-open]')
    ) {
      if (document.activeElement) (document.activeElement as HTMLElement).blur()
      this.setState(false)
      return
    }

    if (target === this.element || (nav && !nav.contains(target))) {
      if (document.activeElement) (document.activeElement as HTMLElement).blur()
      this.setState(false)
    }
  }

  private persistState = () => {
    patch(`/sidebar`, { body: { open: this.open } })
  }
}
